import { byId } from './domUtils';
import { escapeHtml, formatCompact } from './formatUtils';
import { disposeKeywordCloud, renderKeywordCloud, renderKeywordCloudSkeleton } from './keywordCloud';
import type { PlanCode, PrototypeRuntime, Root } from './prototypeTypes';
import { type TopicRecord } from './topicStore';
import { paintTopicChapterSkeleton, paintTopicPrimaryActionLoading, resetTopicOptionalPresentation } from './topicDetailViewLoading';
import { resetTopicInformationBand } from './topicDetailLifecycle';
import { coverUrlForTopic, categoriesFromClassification, DEFAULT_COVER_URL } from '../../domain/topicCover';
import { BROWSE_COVER_BADGE_LABELS, resolveTopicCoverBadge } from '../../domain/topicCoverBadge';
import { topicCategoryLabels } from '../../shared/topicCategoryLabels';
import { applyTopicCover } from './topicCoverLoader';
import { bumpTopicFavoriteVersion, bumpTopicVoteVersion, topicFavoriteVersion, topicVoteVersion } from './topicFavoriteVersion';
import { createTopicChapterRequest } from './topicChapterRequest';

let topicFavoriteReadSequence = 0;
let topicVoteReadSequence = 0;

export function paintTopicRecord(
	root: Root,
	topic: TopicRecord,
	plan: PlanCode,
	target: PrototypeRuntime,
	isCurrentNavigation: () => boolean = () => true,
	initialViewerState: { isFavorite?: boolean } = {},
): void {
	if (!isCurrentNavigation()) return;
	const slug = cleanTopicText(topic.slug) ?? '';
	const title = cleanTopicText(topic.title);
	const subtitle = cleanTopicText(topic.subtitle);
	const category = cleanTopicText(topic.category);
	target.activeCourseId = slug;
	const bannerBg = byId(root, 'topic-banner-bg');
	if (bannerBg) {
		bannerBg.dataset.topicCoverSlug = slug;
		bannerBg.className = `topic-poster-cover topic-cover topic-cover--default ${topicGradient(topic.coverGradient)}`;
		void applyTopicCover(bannerBg, topic.coverImageUrl ?? coverUrlForTopic(categoriesFromClassification(topic.classification)));
	}
	paintTopicPrimaryActionLoading(root, { warmTopic: true });
	paintWarmHero(root, title, subtitle, topicCategoryLabels(category, topic.classification), Boolean(slug));
	paintTopicCoverBadge(root, topic.coverBadge, topic.source, topic.createdAt);
	paintTopicKeywords(root, topic.keywords, { title, subtitle });
	if (topic.engagement) paintTopicMetrics(root, topic.engagement);
	else hideTopicMetrics(root);
	bindTopicFavorite(root, slug, target, initialViewerState.isFavorite);
	bindTopicVote(root, slug, target);
	paintTopicChapterSkeleton(root);
	const viewerScope = `${target.userId ?? ''}|${target.sessionHash ?? ''}`;
	const isCurrentTopicPaint = () => isCurrentNavigation() && `${target.userId ?? ''}|${target.sessionHash ?? ''}` === viewerScope;
	const requestChapters = createTopicChapterRequest(target.api, slug);
	void import('./topicController').then((mod) => {
		if (isCurrentTopicPaint()) mod.renderTopicChapters(root, slug, plan, target, requestChapters, isCurrentTopicPaint);
	});
	void import('./topicDetailHydrator').then((mod) => {
		if (isCurrentTopicPaint()) void mod.hydrateTopicDetail(root, target.api, slug, plan, isCurrentTopicPaint, requestChapters);
	});
}

/**
 * Topic detail must retain the server projection when it already selected a
 * badge for this Topic. In particular, Trending is a ranking snapshot rather
 * than something the client can reconstruct from source or creation time.
 * Older records still use the deterministic GitHub/New fallback.
 */
function paintTopicCoverBadge(
	root: Root,
	coverBadge?: TopicRecord['coverBadge'],
	source?: string | null,
	createdAt?: string | null,
): void {
	const badge = byId(root, 'topic-cover-badge');
	// PC hero (>=641, R8 folded the tablet band into it) re-hosts the badge
	// inside the hero badges row via this twin; the cover-mounted badge keeps
	// serving the phone hero.
	const rowBadge = byId(root, 'topic-cover-badge-row');
	if (!badge && !rowBadge) return;
	const resolved = coverBadge ?? resolveTopicCoverBadge({ source, createdAt }, new Date());
	const apply = (node: HTMLElement | null, row: boolean): void => {
		if (!node) return;
		if (!resolved) {
			node.hidden = true;
			node.className = `cover-badge topic-cover-badge${row ? ' topic-cover-badge--row' : ''}`;
			node.textContent = '';
			return;
		}
		node.hidden = false;
		node.className = `cover-badge topic-cover-badge${row ? ' topic-cover-badge--row' : ''} browse-cover-badge browse-cover-badge--${resolved}${resolved === 'github' ? ' source-badge' : ''}`;
		node.textContent = BROWSE_COVER_BADGE_LABELS[resolved];
	};
	apply(badge, false);
	apply(rowBadge, true);
}

export async function paintTopicFallbackFromChapters(root: Root, slug: string, plan: PlanCode, target: PrototypeRuntime, isCurrentNavigation: () => boolean = () => true): Promise<void> {	if (!isCurrentNavigation()) return;
	const meta = readAiCourseTopicMeta(root, slug);
	try {
		const response = await target.api.listTopicChapters(slug);
		if (!isCurrentNavigation()) return;
		const chapters = response.chapters ?? [];
		target.activeCourseId = response.slug || slug;
	const bannerBg = byId(root, 'topic-banner-bg');
	if (bannerBg) {
		bannerBg.dataset.topicCoverSlug = slug;
		bannerBg.className = 'topic-poster-cover topic-cover topic-cover--default grad-ai';
		void applyTopicCover(bannerBg, DEFAULT_COVER_URL);
	}
	const title = meta?.title ?? 'Private AI Topic';
	paintTopicPrimaryActionLoading(root, { warmTopic: true });
	paintWarmHero(root, title, 'Created in AI Create. Chapters are loaded from your private topic library.', ['AI Create'], true);
		paintTopicCoverBadge(root);
		paintTopicKeywords(root, meta?.keywords ?? [], { title, subtitle: '' });
		paintTopicMetrics(root, { chapters: chapters.length, mediaMinutes: chapters.reduce((sum, chapter) => sum + durationMinutes(chapter.duration), 0), opened: 0, saved: 0 });
		bindTopicFavorite(root, response.slug || slug, target);
		bindTopicVote(root, response.slug || slug, target);
		paintTopicChapterSkeleton(root);
		void import('./topicController').then((mod) => {
			if (isCurrentNavigation()) mod.renderTopicChapters(root, response.slug || slug, plan, target);
		});
	} catch {
		if (!isCurrentNavigation()) return;
		paintTopicUnavailable(root);
		const list = byId(root, 'topicChapterItems');
		if (list) {
			const span = document.createElement('span');
			span.className = 'loading-placeholder';
			span.textContent = 'Chapters could not load. Please try again.';
			list.replaceChildren(span);
		}
	}
}

export function paintTopicLoading(root: Root, topicId: string): void {
	resetTopicInformationBand(root);
	resetTopicOptionalPresentation(root);
	ensureTopicLoadingCover(root, topicId);
	paintTopicCoverBadge(root);
	paintTopicTextSkeleton(root, 'topic-title', 2);
	paintTopicTextSkeleton(root, 'topic-desc', 2);
	const pills = byId(root, 'topic-hero-pills');
	if (pills) {
		// resetTopicOptionalPresentation clears stale category content. Rebuild
		// its permanent footer slot so loading retains the same left/right footer
		// balance as a resolved Topic instead of leaving reactions stranded left.
		const category = document.createElement('span');
		category.id = 'topic-cat-pill';
		category.className = 'pill pill-category topic-cat-pill--primary topic-hero-loading-pill';
		category.dataset.bind = 'topic.category';
		category.appendChild(createTopicLoadingLine('62px'));
		pills.replaceChildren(category);
		pills.hidden = false;
	}
	markTopicMetricsLoading(root);
	const actions = byId(root, 'topic-hero-actions');
	if (actions) actions.hidden = false;
	// Keep the permanent keyword card and cloud host mounted so the Hero
	// silhouette cannot collapse while detail data arrives.
	const outcomes = byId(root, 'topic-outcomes-block');
	const cloud = byId(root, 'topic-keyword-cloud');
	if (outcomes) {
		outcomes.hidden = false;
	}
	if (cloud) renderKeywordCloudSkeleton(cloud);
	paintTopicChapterSkeleton(root);
}

/** Leaves the permanent Hero structure intact while resolving a failed Topic request. */
export function paintTopicUnavailable(root: Root): void {
	const priorAction = byId(root, 'topicPrimaryCta');
	const priorActions = byId(root, 'topic-hero-actions');
	const warmDirectoryFallback = priorAction?.dataset.topicWarmActionPending === 'true' && priorActions && !priorActions.hidden;
	const safeAction = priorAction && priorActions && !priorActions.hidden && !priorAction.classList.contains('loading')
		&& priorAction.getAttribute('href') === '#topicChapterList' && !priorAction.hasAttribute('data-proto-click') && priorAction.textContent?.trim()
		? { label: priorAction.textContent.trim(), href: priorAction.getAttribute('href'), protoAction: priorAction.getAttribute('data-proto-click') }
		: null;
	resetTopicOptionalPresentation(root);
	const title = byId(root, 'topic-title');
	if (title) {
		title.classList.remove('topic-hero-loading');
		title.textContent = 'Topic unavailable';
	}
	const description = byId(root, 'topic-desc');
	if (description) {
		description.classList.remove('topic-hero-loading');
		description.textContent = 'We could not load this topic. Please try again.';
		description.hidden = false;
	}
	const category = byId(root, 'topic-cat-pill');
	if (category) category.classList.remove('topic-hero-loading-pill');
	const action = byId(root, 'topicPrimaryCta');
	if (action) {
		action.classList.remove('loading');
		action.setAttribute('aria-busy', 'false');
		action.removeAttribute('aria-label');
		if (safeAction) {
			action.replaceChildren(document.createTextNode(safeAction.label));
			if (safeAction.href) action.setAttribute('href', safeAction.href);
			if (safeAction.protoAction) action.setAttribute('data-proto-click', safeAction.protoAction);
			byId(root, 'topic-hero-actions')!.hidden = false;
		} else if (warmDirectoryFallback) {
			action.replaceChildren(document.createTextNode('View chapters'));
			action.setAttribute('href', '#topicChapterList');
			byId(root, 'topic-hero-actions')!.hidden = false;
		} else action.replaceChildren();
	}
	hideTopicMetrics(root);
	const chapters = byId(root, 'topicChapterItems');
	if (chapters) {
		const message = document.createElement('p');
		message.className = 'topic-unavailable-message';
		message.textContent = 'Chapters are unavailable.';
		chapters.replaceChildren(message);
	}
}

function paintTopicMetrics(root: Root, metrics: { chapters?: number; mediaMinutes?: number; opened?: number; saved?: number }): void {
	const values: Array<[string, string]> = [
		['topic-chapters', `${formatCompact(metrics.chapters ?? 0)} chapters`],
		['topic-duration', `${formatCompact(metrics.mediaMinutes ?? 0)} min total`],
	];
	for (const [id, value] of values) {
		const node = byId(root, id);
		if (!node) continue;
		node.hidden = false;
		node.classList.remove('tdb-metric--loading');
		if (node.dataset.metricValue === value) continue;
		node.dataset.metricValue = value;
		node.innerHTML = (metricIcons[id] ?? '') + '<span>' + escapeHtml(id === 'topic-duration' ? value.replace(/ total$/u, '') : value)
			+ (id === 'topic-duration' && value.endsWith(' total') ? '<span class="topic-metric-total"> total</span>' : '') + '</span>';
	}
	byId(root, 'topic-hero-metrics')?.removeAttribute('hidden');
}

function hideTopicMetrics(root: Root): void {
	for (const id of ['topic-chapters', 'topic-duration', 'topic-opened', 'topic-favorites']) {
		const node = byId(root, id);
		if (!node) continue;
		node.hidden = true;
		node.replaceChildren();
		delete node.dataset.metricValue;
		node.classList.remove('tdb-metric--loading');
	}
	byId(root, 'topic-hero-metrics')?.setAttribute('hidden', '');
}

function markTopicMetricsLoading(root: Root): void {
	for (const id of ['topic-chapters', 'topic-duration', 'topic-opened', 'topic-favorites']) {
		const node = byId(root, id);
		if (!node) continue;
		node.hidden = false;
		node.textContent = '';
		delete node.dataset.metricValue;
		node.classList.add('tdb-metric--loading');
	}
	byId(root, 'topic-hero-metrics')?.removeAttribute('hidden');
}

function bindTopicFavorite(root: Root, slug: string, target: PrototypeRuntime, initialFavorite: boolean | undefined = undefined): void {
	const button = byId(root, 'topicFavBtn');
	if (!button) return;
	const favoriteButton = button as HTMLButtonElement;
	button.dataset.topicId = slug;
	button.setAttribute('data-proto-click', `toggleStar(this,'${slug}')`);
	const isKnown = typeof initialFavorite === 'boolean';
	// A Source card's state is safe to use immediately. For direct navigation
	// without that state, prevent a default-false click until the read resolves.
	paintTopicFavoriteButton(root, button, isKnown ? initialFavorite : button.getAttribute('aria-pressed') === 'true');
	favoriteButton.disabled = !isKnown;
	button.setAttribute('aria-busy', String(!isKnown));
	const readId = String(++topicFavoriteReadSequence);
	const stateVersionAtRead = topicFavoriteVersion(root, slug);
	button.dataset.topicFavoriteReadId = readId;
	target.api.listFavorites().then((response) => {
		// Do not let an older membership read overwrite either an authoritative
		// detail response or a direct favorite write made after this binding.
		if (button.dataset.topicId !== slug || button.dataset.topicFavoriteReadId !== readId || topicFavoriteVersion(root, slug) !== stateVersionAtRead) return;
		paintTopicFavoriteButton(root, button, new Set((response.favorites ?? []).map((favorite) => favorite.topicSlug)).has(slug));
		favoriteButton.disabled = false;
		button.setAttribute('aria-busy', 'false');
	}).catch(() => {
		if (button.dataset.topicId === slug && button.dataset.topicFavoriteReadId === readId) button.setAttribute('aria-busy', 'false');
	});
}

function bindTopicVote(root: Root, slug: string, target: PrototypeRuntime): void {
	const like = byId(root, 'topicLikeBtn');
	const dislike = byId(root, 'topicDislikeBtn');
	if (!like || !dislike) return;
	like.dataset.topicId = slug;
	dislike.dataset.topicId = slug;
	ensureTopicReactionSparkles(like);
	like.setAttribute('data-proto-click', `toggleVote(this,'${slug}',1)`);
	dislike.setAttribute('data-proto-click', `toggleVote(this,'${slug}',-1)`);
	const readId = String(++topicVoteReadSequence);
	const stateVersionAtRead = topicVoteVersion(root, slug);
	like.dataset.topicVoteReadId = readId;
	dislike.dataset.topicVoteReadId = readId;
	target.api.listTopicVotes().then((response) => {
		if (
			like.dataset.topicId !== slug ||
			like.dataset.topicVoteReadId !== readId ||
			topicVoteVersion(root, slug) !== stateVersionAtRead
		) return;
		const vote = (response.votes ?? []).find((item) => item.topicId === slug);
		paintTopicVote(root, vote?.voteValue);
	}).catch(() => undefined);
}

/** Applies viewer-specific state supplied by the authoritative detail response. */
export function paintTopicViewerState(root: Root, detail: { isFavorite?: boolean; voteValue?: number | null }): void {
	paintTopicFavorite(root, detail.isFavorite);
	paintTopicVote(root, detail.voteValue);
}

function paintTopicFavorite(root: Root, isFavorite: boolean | undefined): void {
	if (typeof isFavorite !== 'boolean') return;
	const favorite = byId(root, 'topicFavBtn');
	if (favorite) {
		bumpTopicFavoriteVersion(root, favorite.dataset.topicId ?? '');
		paintTopicFavoriteButton(root, favorite, isFavorite);
	}
}

function paintTopicFavoriteButton(root: Root, button: HTMLElement, isFavorite: boolean): void {
	button.classList.toggle('active', isFavorite);
	button.classList.toggle('starred', isFavorite);
	button.setAttribute('aria-pressed', String(isFavorite));
	button.dataset.topicFavoriteStateVersion = String(topicFavoriteVersion(root, button.dataset.topicId ?? ''));
}

function paintTopicVote(root: Root, voteValue: number | null | undefined): void {
	const like = byId(root, 'topicLikeBtn');
	const dislike = byId(root, 'topicDislikeBtn');
	const topicId = like?.dataset.topicId ?? dislike?.dataset.topicId;
	if (topicId) bumpTopicVoteVersion(root, topicId);
	like?.classList.toggle('active', voteValue === 1);
	dislike?.classList.toggle('active', voteValue === -1);
	like?.setAttribute('aria-pressed', String(voteValue === 1));
	dislike?.setAttribute('aria-pressed', String(voteValue === -1));
}

function ensureTopicReactionSparkles(button: HTMLElement): void {
	if (button.querySelector('.topic-reaction-sparkles')) return;
	const sparkles = document.createElement('span');
	sparkles.className = 'topic-reaction-sparkles';
	sparkles.setAttribute('aria-hidden', 'true');
	for (let index = 0; index < 7; index += 1) {
		const sparkle = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
		sparkle.setAttribute('viewBox', '0 0 24 24');
		sparkle.setAttribute('fill', 'none');
		sparkle.setAttribute('stroke', 'currentColor');
		sparkle.setAttribute('stroke-width', '2');
		sparkle.innerHTML = '<path d="m12 3-1.5 5.5L5 10l5.5 1.5L12 17l1.5-5.5L19 10l-5.5-1.5Z"></path>';
		sparkles.appendChild(sparkle);
	}
	button.appendChild(sparkles);
}

function paintTopicKeywords(root: Root, keywords: Array<{ keyword: string; coefficient: number }> | undefined, _topic: { title?: string; subtitle?: string }): void {
	const cloud = byId(root, 'topic-keyword-cloud');
	const card = byId(root, 'topic-outcomes-block');
	if (!cloud || !card) return;
	const items = normalizeTopicKeywords(keywords);
	if (!items.length) {
		disposeKeywordCloud(cloud);
		cloud.replaceChildren();
		cloud.classList.remove('kw-cloud-3d', 'kw-cloud--skeleton', 'topic-keyword-cloud--static');
		card.hidden = true;
		const mobileBlock = byId(root, 'topic-keywords-mobile-block') as HTMLDetailsElement | null;
		if (mobileBlock) mobileBlock.hidden = true;
		return;
	}
	card.hidden = false;
	renderKeywordCloud(cloud, items);
	const mobileBlock = byId(root, 'topic-keywords-mobile-block') as HTMLDetailsElement | null;
	const mobileCloud = byId(root, 'topic-keyword-cloud-mobile');
	if (mobileBlock && mobileCloud) {
		mobileBlock.hidden = false;
		mobileBlock.open = false;
		mobileCloud.replaceChildren(...items.map((item) => {
			const chip = document.createElement('span');
			chip.className = 'topic-keyword-static';
			chip.style.setProperty('--kw-weight', String(Math.max(0, Math.min(1, item.coefficient / 10))));
			chip.textContent = item.keyword;
			return chip;
		}));
	}
}

function paintWarmHero(root: Root, title: string | undefined, subtitle: string | undefined, categories: string[], hasAction: boolean): void {
	const titleElement = byId(root, 'topic-title');
	if (titleElement) {
		titleElement.classList.remove('topic-hero-loading');
		titleElement.textContent = title ?? 'Topic not found';
	}
	const description = byId(root, 'topic-desc');
	if (description) {
		description.classList.remove('topic-hero-loading');
		description.textContent = subtitle ?? '';
		description.hidden = !subtitle;
	}
	const pills = byId(root, 'topic-hero-pills');
	if (pills) {
		pills.replaceChildren(...categories.map((category, index) => {
			const pill = document.createElement('span');
			pill.className = `pill pill-category${index === 0 ? ' topic-cat-pill--primary' : ''}`;
			if (index === 0) {
				pill.id = 'topic-cat-pill';
				pill.dataset.bind = 'topic.category';
			}
			pill.textContent = category;
			return pill;
		}));
		pills.hidden = categories.length === 0;
	}
	const actions = byId(root, 'topic-hero-actions');
	if (actions) actions.hidden = !hasAction;
	// The warm poster has real copy, but the primary destination is not known
	// until the chapter/resume reads settle. Its loading CTA is installed by the
	// caller and must remain geometrically stable here.
}

function ensureTopicLoadingCover(root: Root, topicId: string): void {
	const banner = byId(root, 'topic-banner-bg');
	if (!banner || hasKnownTopicCover(banner, topicId)) return;
	banner.dataset.topicCoverSlug = topicId;
	banner.className = 'topic-poster-cover topic-cover topic-cover--default grad-ai';
	void applyTopicCover(banner, DEFAULT_COVER_URL);
}

function hasKnownTopicCover(banner: HTMLElement, topicId: string): boolean {
	return banner.dataset.topicCoverSlug === topicId
		&& (Boolean(banner.style.backgroundImage) || banner.classList.contains('topic-cover--ready'));
}

function paintTopicTextSkeleton(root: Root, id: string, lineCount: number): void {
	const element = byId(root, id);
	if (!element) return;
	element.hidden = false;
	element.classList.add('topic-hero-loading');
	element.replaceChildren(...Array.from({ length: lineCount }, (_, index) => createTopicLoadingLine(index === lineCount - 1 ? '58%' : '100%')));
}

function createTopicLoadingLine(width: string): HTMLElement {
	const line = document.createElement('span');
	line.className = 'topic-hero-loading-line';
	line.setAttribute('aria-hidden', 'true');
	// Only width is inline; height/margin come from CSS (1lh line box) so the
	// bars match the real text lines in every font context. The shimmer
	// background (linear-gradient + background-size + animation) is also set
	// by the CSS class. An inline `background:` shorthand would reset
	// background-image to none and kill the keyframe animation - the line
	// would still report animation: running but render as a solid color.
	line.style.cssText = `display:block;width:${width};`;
	return line;
}

function readAiCourseTopicMeta(root: Root, slug: string): { title: string; keywords?: Array<{ keyword: string; coefficient: number }> } | null {
	return (root as unknown as { __aiCourseTopicMeta?: Record<string, { title: string; keywords?: Array<{ keyword: string; coefficient: number }> }> }).__aiCourseTopicMeta?.[slug] ?? null;
}

function normalizeTopicKeywords(value: unknown): Array<{ keyword: string; coefficient: number }> {
	if (!Array.isArray(value)) return [];
	return value.flatMap((item) => {
		if (typeof item?.keyword !== 'string' || item.keyword.trim().length === 0 || typeof item.coefficient !== 'number') return [];
		return [{ keyword: item.keyword, coefficient: item.coefficient }];
	});
}

function durationMinutes(duration: string | null | undefined): number {
	const match = String(duration ?? '').match(/\d+/);
	return match ? Number.parseInt(match[0], 10) : 0;
}

function cleanTopicText(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function topicGradient(value: unknown): string {
	const token = cleanTopicText(value);
	return token && topicGradients.has(token) ? token : 'grad-ai';
}

const topicGradients = new Set(['grad-ai', 'grad-startup', 'grad-marketing', 'grad-programming', 'grad-design', 'grad-business', 'grad-rag', 'grad-saas', 'grad-email', 'grad-growth']);
const metricIcons: Record<string, string> = {
	'topic-chapters': '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="metric-icon"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>',
	'topic-duration': '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="metric-icon"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>',
};
